import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NumberRating from 'components/Common/NumberRating'
import UserReviewSection from 'components/OfferPage/NavigableOfferContent/UserReviewSection'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import useDrawerDividerKind from '../../hooks/useDrawerDividerKind'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import PropertyAdditionalDescription from 'components/AccommodationKit/PropertyAdditionalDescription'
import PropertyFacilitiesContent from 'components/AccommodationKit/PropertyFacilitiesContent'
import PropertyPopularFacilities from 'components/AccommodationKit/PropertyPopularFacilities'
import HotelDetailAmenities from './HotelDetailAmenities'
import HotelDetailPricing from 'tripPlanner/components/ItemOfferPrice/HotelDetailPricing'
import HotelDetailRoomOptions from './HotelDetailRoomOptions'
import HotelDetailBookedRoom from './HotelDetailBookedRoom'
import DrawerAccommodationDetailsSections from './DrawerAccommodationDetailsSections'
import { ContentWrapper } from './HotelListingDrawer.styles'
import useHotelDetailCompactLayout from './useHotelDetailCompactLayout'
import {
  OfferDetailImageGallery,
  OfferDetailDescription,
  OfferDetailReviews,
  OfferDetailMap,
  OfferDetailBreadcrumbs,
} from '../OfferDetail'
import { splitDrawerDetailHero } from '../drawerDetailHeroLayout'
import type { OfferDetailReview } from '../OfferDetail'
import type { DrawerHotel } from './types'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import type { TripItineraryData, TripStop, MapCameraPosition } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { DrawerDates } from './useNotifyDatesChange'
import useDrawerAccommodationDetailsSource from './useDrawerAccommodationDetailsSource'
import useDrawerHotelBreadcrumbs from './useDrawerHotelBreadcrumbs'
import useDrawerPropertyOverview from './useDrawerPropertyOverview'
import getDrawerReviewContext from './getDrawerReviewContext'
import { useOfferDrawerOpenedDispatch } from '../../hooks/useOfferDrawerOpenedDispatch'

const DrawerFlashHotelRoomOptions = lazy(
  () => import('./DrawerFlashHotelRoomOptions'),
)
const DrawerBedbankRoomOptions = lazy(
  () => import('./DrawerBedbankRoomOptions'),
)
const DrawerAlwaysOnRoomOptions = lazy(
  () => import('./DrawerAlwaysOnRoomOptions'),
)

export type HotelDetailPricingState = 'ready' | 'loading' | 'error'

interface Props {
  hotel: DrawerHotel
  titleInContent?: string
  showLocationInContent?: boolean
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
  pricingState?: HotelDetailPricingState
  isBooked?: boolean
  orderId?: string
  savedBookedRooms?: Array<DrawerHotelBookedRoom>
  itineraryData?: TripItineraryData
  contextStop?: TripStop
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
  checkIn?: string
  checkOut?: string
  onDatesChange?: (dates: DrawerDates) => void
  initialOccupancies?: Array<App.Occupants>
  selectedPackageId?: string
  selectedRoomTypeId?: string
  selectedRateId?: string
  selectedCheckIn?: string
  selectedCheckOut?: string
  selectedCharterOriginAirportCode?: string
  selectedCharterFareClass?: string
  scrollToRoomOptions?: boolean
  checkoutSource?: App.CheckoutSource
  onEnlargeMap?: (camera: MapCameraPosition) => void
}

function HotelDetailView({
  hotel,
  titleInContent,
  showLocationInContent = true,
  reviews,
  reviewsLoading,
  pricingState = 'ready',
  isBooked,
  orderId,
  savedBookedRooms,
  itineraryData,
  contextStop,
  onSendMessage,
  onAfterAction,
  checkIn,
  checkOut,
  onDatesChange,
  initialOccupancies,
  selectedPackageId,
  selectedRoomTypeId,
  selectedRateId,
  selectedCheckIn,
  selectedCheckOut,
  selectedCharterOriginAirportCode,
  selectedCharterFareClass,
  scrollToRoomOptions,
  checkoutSource,
  onEnlargeMap,
}: Props) {
  const [pickerSelection, setPickerSelection] = useState<{ offerId: string; params: OfferClickSearchParams }>()
  const handleSearchParamsChange = useCallback((params: OfferClickSearchParams) => {
    setPickerSelection({ offerId: hotel.offerId, params })
  }, [hotel.offerId])
  const liveSearchParams = pickerSelection?.offerId === hotel.offerId ? pickerSelection.params : undefined
  const breadcrumbSearchParams = useMemo(() => liveSearchParams ?? {
    checkIn, checkOut, rooms: initialOccupancies,
  }, [liveSearchParams, checkIn, checkOut, initialOccupancies])
  const intl = useIntl()
  const dividerKind = useDrawerDividerKind()
  const {
    name,
    location,
    description,
    propertyOverview: offerDetailPropertyOverview,
    images,
    rating,
    reviewsTotal,
    amenities,
    price,
    isTotal,
    includesTaxesAndFees,
    unavailableReason,
    luxPlusPrice,
    value,
    memberValue,
    discountPercent,
    memberDiscountPercent,
    offerId,
    deeplinkUrl,
    geoLocation,
    saleUnit,
  } = hotel

  // This is the shared presentational component behind both ChatOfferDetailDrawer (chat/carousel/
  // itinerary path) and HotelListingDrawer (the hotel search drawer's own inline detail view), so
  // dispatching offer_drawer_opened here covers every way a customer can end up looking at a
  // hotel's details, current and future, without each caller needing to remember to wire it up
  // itself. See useOfferDrawerOpenedDispatch for the dedup rationale.
  useOfferDrawerOpenedDispatch(offerId, name, hotel.offerType ?? 'hotel')

  const { propertyOverview, isLoading: propertyOverviewLoading } = useDrawerPropertyOverview(
    offerId,
    hotel.offerType,
    offerDetailPropertyOverview,
  )
  const propertyDescription = propertyOverview?.description ?? description
  const hasRating = rating !== undefined && rating > 0
  const hasAmenities = !!amenities && amenities.length > 0
  const hasPopularFacilities = !!propertyOverview?.popularFacilities.length
  const hasPropertyFacilities = !!propertyOverview && (
    !!propertyOverview.facilities || propertyOverview.amenityGroups.length > 0
  )
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation
  const roomOptions = !isBooked ? hotel.roomOptions : undefined
  const hasRoomOptions = !!roomOptions && roomOptions.length > 0
  const resolvedBookedRooms = savedBookedRooms ?? hotel.bookedRooms
  const hasBookedRooms =
    isBooked && !!resolvedBookedRooms && resolvedBookedRooms.length > 0
  // Always-On offers use accommodation property rates; flash offers use public-offer packages.
  const isFlashHotel = !isBooked && hotel.offerType === OFFER_TYPE_HOTEL
  const isAlwaysOnHotel = !isBooked && hotel.offerType === OFFER_TYPE_ALWAYS_ON
  const isBedbankHotel = !isBooked && hotel.offerType === OFFER_TYPE_BED_BANK
  const accommodationDetailsSource = useDrawerAccommodationDetailsSource(
    offerId,
    hotel.offerType,
    !!isBooked,
  )
  const reviewContext = getDrawerReviewContext(accommodationDetailsSource)
  const pricingOffer = accommodationDetailsSource.kind === 'unsupported'
    ? undefined
    : accommodationDetailsSource.offer
  const breadcrumbs = useDrawerHotelBreadcrumbs(pricingOffer, checkIn, checkOut)
  const shouldShowUnsupportedPropertyFacilities =
    accommodationDetailsSource.kind === 'unsupported' &&
    hotel.offerType !== OFFER_TYPE_BED_BANK &&
    !!propertyOverview &&
    !hasPopularFacilities &&
    hasPropertyFacilities
  const facilitiesTitle = intl.formatMessage({
    id: 'hBw9KVRIf4JdjxDK2gN_',
    defaultMessage: 'Facilities',
    description: 'Heading for the facilities section on a single property page',
  })
  const shouldShowAmenities = hasAmenities && ![
    OFFER_TYPE_HOTEL,
    OFFER_TYPE_BED_BANK,
    OFFER_TYPE_ALWAYS_ON,
  ].includes(hotel.offerType ?? '')
  const contentRef = useRef<HTMLDivElement>(null)
  const isCompactDrawer = useHotelDetailCompactLayout(contentRef)
  const mobileFullWidthGallery = Boolean(titleInContent) || isCompactDrawer
  const hasSelectedDates = !!checkIn && !!checkOut
  const shouldShowPricing = isBooked || pricingState === 'loading' || (
    pricingState === 'ready' && (!!price || (hasSelectedDates && !!unavailableReason))
  )
  const heroGallery = images.length > 0 ? (
    <OfferDetailImageGallery
      images={images}
      mobileFullWidth={mobileFullWidthGallery}
    />
  ) : null
  const { leadingHero, inlineHero } = splitDrawerDetailHero(mobileFullWidthGallery, heroGallery)

  return (
    <>
      {leadingHero}
      <ContentWrapper>
        <Group ref={contentRef} direction="vertical" gap={24}>
          {!isCompactDrawer && <OfferDetailBreadcrumbs
            items={breadcrumbs}
            vertical="hotel"
            searchParams={breadcrumbSearchParams}
          />}
          {inlineHero}

          {(titleInContent || showLocationInContent || isBooked || hasRating) && (
            <Group direction="vertical" gap={12}>
              {titleInContent && <Heading variant="heading2">{titleInContent}</Heading>}
              {(showLocationInContent || isBooked) && (
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  {showLocationInContent && (
                    <BodyText variant="medium" colour="light-foreground-alt">
                      {location}
                    </BodyText>
                  )}
                  {isBooked && <BookedLabel />}
                </Group>
              )}
              {hasRating && (
                <NumberRating
                  rating={rating}
                  total={reviewsTotal}
                  variant="small"
                  squareContainer
                />
              )}
            </Group>
          )}

          {propertyDescription && <OfferDetailDescription description={propertyDescription} />}
          {propertyOverview?.additionalDescription && (
            <PropertyAdditionalDescription description={propertyOverview.additionalDescription} />
          )}

          {propertyOverview && hasPopularFacilities && (
            <PropertyPopularFacilities
              popularFacilities={propertyOverview.popularFacilities}
              facilities={propertyOverview.facilities}
              amenities={propertyOverview.amenityGroups}
            />
          )}

          {shouldShowUnsupportedPropertyFacilities && (
            <Group direction="vertical" gap={8}>
              <Heading variant="heading3" as="h3">{facilitiesTitle}</Heading>
              {propertyOverview.facilities ? (
                <PropertyFacilitiesContent facilities={propertyOverview.facilities} />
              ) : (
                <PropertyFacilitiesContent amenities={propertyOverview.amenityGroups} />
              )}
            </Group>
          )}

          {shouldShowPricing && (
            <>
              <Divider kind={dividerKind} />
              {pricingState === 'loading' && !isBooked ? (
                <Group
                  data-testid="hotel-detail-pricing-loading"
                  direction="vertical"
                  gap={8}
                >
                  <PriceRowPriceCaptionLoadingSkeleton />
                  <PriceRowPriceLoadingSkeleton size="L" />
                </Group>
              ) : (
                <HotelDetailPricing
                  price={price}
                  luxPlusPrice={luxPlusPrice}
                  value={value}
                  memberValue={memberValue}
                  discountPercent={discountPercent}
                  memberDiscountPercent={memberDiscountPercent}
                  isTotal={isTotal}
                  includesTaxesAndFees={includesTaxesAndFees}
                  unavailableReason={unavailableReason}
                  offerId={offerId}
                  saleUnit={saleUnit}
                  isBooked={isBooked}
                  orderId={orderId}
                  offer={pricingOffer}
                />
              )}
            </>
          )}

          {!propertyOverviewLoading && !propertyOverview && shouldShowAmenities && (
            <>
              <Divider kind={dividerKind} />
              <HotelDetailAmenities amenities={amenities} />
            </>
          )}

          {hasBookedRooms && (
            <>
              <Divider kind={dividerKind} />
              <HotelDetailBookedRoom bookedRooms={resolvedBookedRooms} />
            </>
          )}

          {isFlashHotel && (
            <>
              <Divider kind={dividerKind} />
              <Suspense fallback={<LoadingIndicator inline visible />}>
                <DrawerFlashHotelRoomOptions
                  key={offerId}
                  offerId={offerId}
                  source={accommodationDetailsSource.kind === 'flash' ? accommodationDetailsSource : undefined}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onDatesChange={onDatesChange}
                  onSearchParamsChange={handleSearchParamsChange}
                  hotel={hotel}
                  itineraryData={itineraryData}
                  contextStop={contextStop}
                  onSendMessage={onSendMessage}
                  onAfterAction={onAfterAction}
                  initialOccupancies={initialOccupancies}
                  selectedPackageId={selectedPackageId}
                  selectedRoomTypeId={selectedRoomTypeId}
                  selectedCheckIn={selectedCheckIn}
                  selectedCheckOut={selectedCheckOut}
                  selectedCharterOriginAirportCode={
                    selectedCharterOriginAirportCode
                  }
                  selectedCharterFareClass={selectedCharterFareClass}
                  scrollToRoomOptions={scrollToRoomOptions}
                  checkoutSource={checkoutSource}
                />
              </Suspense>
            </>
          )}

          {isAlwaysOnHotel && (
            <>
              <Divider kind={dividerKind} />
              <Suspense fallback={<LoadingIndicator inline visible />}>
                <DrawerAlwaysOnRoomOptions
                  key={offerId}
                  offerId={offerId}
                  source={accommodationDetailsSource.kind === 'property' ? accommodationDetailsSource : undefined}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onDatesChange={onDatesChange}
                  onSearchParamsChange={handleSearchParamsChange}
                  initialOccupancies={initialOccupancies}
                  roomOptions={roomOptions}
                  deeplinkUrl={deeplinkUrl}
                  scrollToRoomOptions={scrollToRoomOptions}
                  hotel={hotel}
                  itineraryData={itineraryData}
                  contextStop={contextStop}
                  onSendMessage={onSendMessage}
                  onAfterAction={onAfterAction}
                />
              </Suspense>
            </>
          )}

          {isBedbankHotel && (
            <>
              <Divider kind={dividerKind} />
              <Suspense fallback={<LoadingIndicator inline visible />}>
                <DrawerBedbankRoomOptions
                  key={offerId}
                  offerId={offerId}
                  source={accommodationDetailsSource.kind === 'bedbank' ? accommodationDetailsSource : undefined}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onDatesChange={onDatesChange}
                  onSearchParamsChange={handleSearchParamsChange}
                  initialOccupancies={initialOccupancies}
                  selectedPackageId={selectedPackageId}
                  selectedRoomTypeId={selectedRoomTypeId}
                  selectedRateId={selectedRateId}
                  selectedCheckIn={selectedCheckIn}
                  selectedCheckOut={selectedCheckOut}
                  hotel={hotel}
                  itineraryData={itineraryData}
                  contextStop={contextStop}
                  onSendMessage={onSendMessage}
                  onAfterAction={onAfterAction}
                  scrollToRoomOptions={scrollToRoomOptions}
                  checkoutSource={checkoutSource}
                />
              </Suspense>
            </>
          )}

          {!isFlashHotel && !isBedbankHotel && !isAlwaysOnHotel && hasRoomOptions && (
            <>
              <Divider kind={dividerKind} />
              <HotelDetailRoomOptions
                roomOptions={roomOptions}
                deeplinkUrl={deeplinkUrl}
              />
            </>
          )}

          {hasGeoLocation && (
            <>
              <Divider kind={dividerKind} />
              <OfferDetailMap geoLocation={geoLocation} offerName={name} offerId={offerId} offerType="hotel" onEnlarge={onEnlargeMap} />
            </>
          )}

          {accommodationDetailsSource.kind === 'flash' && (
            <DrawerAccommodationDetailsSections
              source={accommodationDetailsSource}
              dividerKind={dividerKind}
            />
          )}

          {accommodationDetailsSource.kind === 'bedbank' && (
            <DrawerAccommodationDetailsSections
              source={accommodationDetailsSource}
              dividerKind={dividerKind}
              amenities={amenities}
            />
          )}

          {accommodationDetailsSource.kind === 'property' && (
            <DrawerAccommodationDetailsSections
              source={accommodationDetailsSource}
              dividerKind={dividerKind}
              amenities={amenities}
            />
          )}

          {reviewContext && (
            <>
              <Divider kind={dividerKind} />
              <UserReviewSection
                reviewContext={reviewContext}
                presentation="embedded"
              />
            </>
          )}

          {!reviewContext && (hasReviews || reviewsLoading) && (
            <>
              <Divider kind={dividerKind} />
              {reviewsLoading && !hasReviews && (
                <Group direction="vertical" gap={12}>
                  <LoadingBox height={20} width={120} borderRadius="S" />
                  <LoadingBox height={60} width={320} borderRadius="S" />
                </Group>
              )}
              {hasReviews && (
                <OfferDetailReviews
                  rating={rating}
                  reviewsTotal={reviewsTotal}
                  reviews={reviews}
                />
              )}
            </>
          )}
        </Group>
      </ContentWrapper>
    </>
  )
}

export default HotelDetailView
